// useNodeActivityCount.js

import { useEffect, useState } from 'react';

// Utilities
import { EngagementType } from 'utilities/Enums';
import { shouldIncludeActivityEvent } from 'node/activity/utils/placeholderTitleRename';

// Services
import { subscribeToEngagements } from 'services/engagements/EngagementReadService';
import {
  NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT,
  subscribeToNodeActivityEvents,
} from 'services/nodes/NodeActivityService';

const ACTIVITY_COUNT_EVENTS_LIMIT = Math.max(50, NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT);

/**
 * Live count of Activity story items for tab badges:
 * top-level comments + visible system events (same inclusion rules as the feed).
 * Replies are excluded.
 *
 * @param {string|null|undefined} nodeKey
 * @param {{ typeLabel?: string }} [options]
 * @returns {number}
 */
export default function useNodeActivityCount(nodeKey, { typeLabel = '' } = {}) {
  const [commentCount, setCommentCount] = useState(0);
  const [eventCount, setEventCount] = useState(0);

  useEffect(() => {
    const cleanKey = typeof nodeKey === 'string' ? nodeKey.trim() : '';
    if (!cleanKey) {
      setCommentCount(0);
      return undefined;
    }

    const params = [{ field: 'itemKey', operator: '==', value: cleanKey }];
    const unsubscribe = subscribeToEngagements({ params, sortFields: [] }, (engagements) => {
      const topLevelCount = (Array.isArray(engagements) ? engagements : []).filter((engagement) => (
        engagement?.type === EngagementType.NODE_COMMENT
        && (engagement?.parentKey == null || engagement?.parentKey === '')
        && !engagement?.deleted
      )).length;

      setCommentCount(topLevelCount);
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [nodeKey]);

  useEffect(() => {
    const cleanKey = typeof nodeKey === 'string' ? nodeKey.trim() : '';
    if (!cleanKey) {
      setEventCount(0);
      return undefined;
    }

    const unsubscribe = subscribeToNodeActivityEvents(
      { nodeKey: cleanKey, limit: ACTIVITY_COUNT_EVENTS_LIMIT },
      (events) => {
        const visibleCount = (Array.isArray(events) ? events : []).filter((event) => (
          shouldIncludeActivityEvent(event, { typeLabel })
        )).length;
        setEventCount(visibleCount);
      },
      () => {
        setEventCount(0);
      },
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [nodeKey, typeLabel]);

  return commentCount + eventCount;
}
